import { memo } from 'react';
import { useSelector } from 'react-redux';
import { Typography, makeStyles } from '@material-ui/core';
import { formatNumber } from '../../../utils/formatters';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.colors.blue2,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  chartContainer: {
    marginBottom: theme.spacing(1),
  },
}));

const ChartSection = ({
                        chartData,
                        category,
                        isComparisonMode,
                        metric,
                        baselineCount,
                        followupCount,
                        totalPatients,
                      }) => {
  const classes = useStyles();
  const { en: { populationHealth } } = useSelector((state) => state.i18n);

  const renderChart = () => {
    if (isComparisonMode) {
      // Bar chart for comparison mode with shadows
      return (
        <ResponsiveContainer width="100%" height="100%" style={{ marginLeft: -30 }}>
          <BarChart data={chartData} margin={{ top: 20, right: 0, left: 0, bottom: 0 }}>
            <defs>
              {/* Drop shadow filter */}
              <filter id="dropshadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow
                  dx="0"
                  dy="3"
                  stdDeviation="3"
                  floodColor="rgba(0,0,0,0.15)"
                />
              </filter>
              {/* Gradients for each bar color */}
              <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={category.color} stopOpacity="1" />
                <stop offset="100%" stopColor={category.color} stopOpacity="0.8" />
              </linearGradient>
              <linearGradient id="barGradientBaseline" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={category.color} stopOpacity="0.6" />
                <stop offset="100%" stopColor={category.color} stopOpacity="0.4" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12, fill: '#6B7280' }}
              axisLine={{ stroke: '#E5E7EB' }}
              tickLine={{ stroke: '#E5E7EB' }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#6B7280' }}
              axisLine={{ stroke: '#E5E7EB' }}
              tickLine={{ stroke: '#E5E7EB' }}
            />
            <Bar
              dataKey="value"
              radius={[6, 6, 0, 0]}
              filter="url(#dropshadow)"
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.name === 'Baseline' ? 'url(#barGradientBaseline)' : 'url(#barGradient)'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    } else {
      // Pie chart for normal mode
      return (
        <ResponsiveContainer width="100%" height="100%" style={{ marginLeft: -20 }}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              outerRadius={80}
              innerRadius={40}
              paddingAngle={2}
              dataKey="value"
              labelLine={false}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      );
    }
  };

  return (
    <>
      <Typography className={classes.sectionTitle}>
        {isComparisonMode
          ? (populationHealth?.baselineVsFollowupComparison || 'Baseline vs Follow-up Comparison')
          : (populationHealth?.populationDistribution || 'Population Distribution')
        }
      </Typography>
      <div className={classes.chartContainer} style={{ height: isComparisonMode ? 300 : 200 }}>
        {renderChart()}
      </div>

      {/* Custom Legend */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '16px',
        marginTop: '12px',
        marginLeft: isComparisonMode ? -30 : 0,
      }}>
        {isComparisonMode ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: category.color,
                opacity: 0.6,
                borderRadius: '3px',
              }} />
              <span style={{ fontSize: '12px', color: '#6B7280' }}>
                Baseline ({formatNumber(baselineCount)})
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: category.color,
                borderRadius: '3px',
              }} />
              <span style={{ fontSize: '12px', color: '#6B7280' }}>
                Follow-up ({formatNumber(followupCount)})
              </span>
            </div>
          </>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            width: '100%',
            marginLeft: 50,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: category.color,
                borderRadius: '3px',
              }} />
              <span style={{ fontSize: '12px', color: '#6B7280' }}>
                {metric.label} ({formatNumber(metric.count)})
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: '#E5E7EB',
                borderRadius: '3px',
              }} />
              <span style={{ fontSize: '12px', color: '#6B7280' }}>
                Other Patients ({formatNumber(totalPatients - metric.count)})
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default memo(ChartSection);
